/**
 * @fileoverview Students panel component for available and assigned students
 */
import type { FC } from 'react'
import { useArcTranslation } from 'hooks'
import { Stack, Group, Text, Box, Input, Button, Center } from 'components'
import { ScrollArea } from '@mantine/core'
import { IconSearch, IconPlus, IconUserOff, IconUsersGroup } from '@tabler/icons-react'
import { useAssignProjectContext } from './AssignProjectContext'
import { StudentListItem } from './StudentListItem'
import { EmptyAssignedStudents } from './EmptyAssignedStudents'
import { SPACING, CARD_STYLES, SEARCH_INPUT_STYLES, SCROLL_STYLES, COLORS } from './styles'
import type { Student, ProjectGroup } from './types'

interface StudentsPanelProps {
  availableStudents: Student[]
  assignedStudents: Student[]
  totalStudents: number
  groups: ProjectGroup[]
  isMobile: boolean
}

const StudentCard: FC<{ children: React.ReactNode; flex?: boolean; variant?: 'default' | 'plain' }> = ({ children, flex, variant = 'default' }) => (
  <Box
    sx={{
      ...(variant === 'default' ? CARD_STYLES : { borderRadius: 12 }),
      ...(flex && { flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }),
    }}
    p={SPACING.element}
  >
    {children}
  </Box>
)

const SectionTitle: FC<{ title: string; count?: number; mb?: number }> = ({ title, count, mb: marginBottom }) => (
  <Text size="sm" weight={600} color="#1a1a1a" mb={marginBottom}>
    {title}
    {count !== undefined && count > 0 && (
      <Text component="span" color="dimmed" weight={400} ml={8}>({count})</Text>
    )}
  </Text>
)

const AvailableStudentsPanel: FC<{
  students: Student[]
  allAssigned: boolean
  hasGroupSelected: boolean
  isMobile: boolean
}> = ({ students, allAssigned, hasGroupSelected, isMobile }) => {
  const { t } = useArcTranslation()
  const { searchQuery, setSearchQuery, addStudent, addAllStudents } = useAssignProjectContext()

  const handleAddAllStudents = () => {
    addAllStudents(students.map(s => s.id))
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.currentTarget.value)

  const hasStudents = students.length > 0

  if (!hasGroupSelected) {
    return (
      <StudentCard flex={!isMobile} variant="plain">
        <Center sx={{ flex: 1 }}>
          <Stack align="center" spacing="xs">
            <IconUsersGroup size={24} color="#999" stroke={1.5} />
            <Text size="sm" color="dimmed" ta="center">
              {t('assign_project.select_group_first')}
            </Text>
          </Stack>
        </Center>
      </StudentCard>
    )
  }

  if (!hasStudents) {
    return (
      <StudentCard flex={!isMobile} variant="plain">
        <Center sx={{ flex: 1 }}>
          <Stack align="center" spacing="xs">
            <IconUserOff size={24} color="#999" stroke={1.5} />
            <Text size="sm" color="dimmed" ta="center">
              {t(allAssigned ? 'assign_project.all_students_assigned' : 'assign_project.no_available_students')}
            </Text>
          </Stack>
        </Center>
      </StudentCard>
    )
  }

  return (
    <StudentCard flex={!isMobile} variant="plain">
      <Group position="apart" mb={12}>
        <SectionTitle title={t('assign_project.your_students')} />
        <Button
          variant="subtle"
          compact
          leftIcon={<IconPlus size={14} />}
          onClick={handleAddAllStudents}
          styles={{
            root: {
              color: COLORS.primary,
              padding: '4px 8px',
              '&:hover': { backgroundColor: COLORS.primarySoft },
            },
          }}
        >
          {t('assign_project.add_all_students')}
        </Button>
      </Group>
      <Input
        icon={<IconSearch size={16} />}
        placeholder={t('assign_project.search_by_name')}
        value={searchQuery}
        onChange={handleSearchChange}
        size="sm"
        radius="xl"
        styles={SEARCH_INPUT_STYLES}
        mb={12}
      />
      <ScrollArea sx={{ flex: 1, minHeight: 0 }} type="auto" offsetScrollbars styles={SCROLL_STYLES}>
        <Stack spacing={2}>
          {students.map(student => (
            <StudentListItem key={student.id} student={student} onAction={() => addStudent(student.id)} actionType="add" />
          ))}
        </Stack>
      </ScrollArea>
    </StudentCard>
  )
}

const AssignedStudentsPanel: FC<{
  students: Student[]
  isMobile: boolean
}> = ({ students, isMobile }) => {
  const { t } = useArcTranslation()
  const { removeStudent } = useAssignProjectContext()

  return (
    <StudentCard flex={!isMobile}>
      <SectionTitle title={t('assign_project.assigned_students')} count={students.length} mb={12} />
      <Box sx={!isMobile ? { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 } : { minHeight: 200 }}>
        {students.length === 0 ? (
          <EmptyAssignedStudents />
        ) : (
          <ScrollArea
            sx={{ flex: 1, minHeight: 0 }}
            type="auto"
            offsetScrollbars
            styles={SCROLL_STYLES}
          >
            <Stack spacing={2}>
              {students.map(student => (
                <StudentListItem key={student.id} student={student} onAction={() => removeStudent(student.id)} actionType="remove" />
              ))}
            </Stack>
          </ScrollArea>
        )}
      </Box>
    </StudentCard>
  )
}

export const StudentsPanel: FC<StudentsPanelProps> = ({
  availableStudents,
  assignedStudents,
  totalStudents,
  groups,
  isMobile,
}) => {
  const { selectedGroupId } = useAssignProjectContext()
  const hasGroupSelected = selectedGroupId !== null
  const allAssigned = totalStudents > 0 && availableStudents.length === 0

  if (isMobile) {
    return (
      <Stack spacing={SPACING.element}>
        <AvailableStudentsPanel students={availableStudents} allAssigned={allAssigned} hasGroupSelected={hasGroupSelected} isMobile />
        <AssignedStudentsPanel students={assignedStudents} isMobile />
      </Stack>
    )
  }

  return (
    <Group align="stretch" spacing={SPACING.element} noWrap sx={{ flex: 1, overflow: 'hidden' }} mih={0}>
      <AvailableStudentsPanel students={availableStudents} allAssigned={allAssigned} hasGroupSelected={hasGroupSelected} isMobile={false} />
      <AssignedStudentsPanel students={assignedStudents} isMobile={false} />
    </Group>
  )
}
